// Shared merge-on-save machinery for the Playbook forms (2026-07 DI UI audit,
// workstream W2.1).
//
// THE RULE: saving a config file must never change a key the operator did not
// edit. Forms model a subset of fields; everything else must survive a save
// byte-for-byte. Each form keeps the raw node it parsed (`_original`) and, on
// save, writes a modeled field only when the form value differs from what the
// original value parses to.
//
// The contract test for every consumer is round-trip stability over the real
// production config corpus in `__fixtures__/config/`.

export const clone = (value) =>
    value === undefined ? undefined : JSON.parse(JSON.stringify(value))

export const canonEqual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// Field specs: toForm maps a JSON value into a form value; fromForm maps back.
// fromForm returning undefined means "delete the key".
export const specs = {
    text: {
        toForm: (v) => (typeof v === 'string' ? v : ''),
        fromForm: (v) => (v && String(v).trim() !== '' ? v : undefined),
    },
    bool: {
        toForm: (v) => (typeof v === 'boolean' ? v : undefined),
        fromForm: (v) => (typeof v === 'boolean' ? v : undefined),
    },
    number: {
        toForm: (v) => (typeof v === 'number' ? v : ''),
        fromForm: (v) => {
            if (v === '' || v === null || v === undefined) return undefined
            const num = Number(v)
            return Number.isNaN(num) ? undefined : num
        },
    },
    csvArray: {
        toForm: (v) => (Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : ''),
        fromForm: (v) => {
            if (Array.isArray(v)) return v.length > 0 ? v : undefined
            if (typeof v !== 'string' || v.trim() === '') return undefined
            return v
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
        },
    },
    jsonText: {
        toForm: (v) => {
            if (v === undefined || v === null) return ''
            if (typeof v === 'object') return JSON.stringify(v, null, 2)
            return String(v)
        },
        fromForm: (v) => {
            if (v === undefined || v === null || String(v).trim() === '') return undefined
            try {
                return JSON.parse(v)
            } catch {
                return v
            }
        },
    },
    newlineArray: {
        toForm: (v) => (Array.isArray(v) ? v.join('\n') : typeof v === 'string' ? v : ''),
        fromForm: (v) => {
            if (typeof v !== 'string' || v.trim() === '') return undefined
            const lines = v
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean)
            return lines.length > 0 ? lines : undefined
        },
    },
}

/**
 * Merge modeled fields into a copy of `original`. A field is written only when
 * the form value differs from what the original value parses to — untouched
 * fields keep the original representation, including absence.
 */
export const mergeRow = (original, formRow, fieldSpecs) => {
    const out = original ? clone(original) : {}
    for (const [key, spec] of Object.entries(fieldSpecs)) {
        const originalAsForm = spec.toForm(original ? original[key] : undefined)
        const formValue = formRow[key]
        if (canonEqual(formValue, originalAsForm)) continue
        const next = spec.fromForm(formValue)
        if (next === undefined) delete out[key]
        else out[key] = next
    }
    return out
}

/** Merge a keyed sub-object; returns undefined (delete) when merge yields {}. */
export const mergeSection = (original, formRow, fieldSpecs) => {
    const merged = mergeRow(original, formRow, fieldSpecs)
    return Object.keys(merged).length > 0 ? merged : undefined
}

/** Build a form row from an original node: `_original` + one entry per spec. */
export const rowFromSpecs = (original, fieldSpecs) => {
    const row = { _original: original ? clone(original) : null }
    for (const [key, spec] of Object.entries(fieldSpecs)) {
        row[key] = spec.toForm(original ? original[key] : undefined)
    }
    return row
}

export const setOrDelete = (target, key, value) => {
    if (value === undefined) delete target[key]
    else target[key] = value
}
